import * as yup from "yup";

import { EarlyRepaymentType, EarlyRepaymentUnit } from "./types";

const NEEDS_FEE = (
  t: EarlyRepaymentType | null | undefined,
): t is EarlyRepaymentType.FEE_APPLIES | EarlyRepaymentType.FREE_AFTER_PERIOD =>
  t === EarlyRepaymentType.FEE_APPLIES ||
  t === EarlyRepaymentType.FREE_AFTER_PERIOD;

// this.path is `undefined` (not "") when the object is validated standalone
// — e.g. a direct schema.validate() in a unit test. Left unguarded, the
// template literal below produces the literal string "undefined.value", a
// path no field owns.
const fieldErrorPath = (base: string | undefined, field: string): string =>
  base ? `${base}.${field}` : field;

/**
 * Cross-field rules. Attached to the object schema below rather than exported
 * for each bid schema to remember: the invariants are properties OF the value
 * object, so they cannot be forgotten at a call site. Errors are attached to
 * the offending field's path so the form shows them in the right place.
 */
const earlyRepaymentConsistency = {
  name: "early-repayment-consistent",
  test(this: yup.TestContext, values: Record<string, unknown> | undefined) {
    if (!values) return true;

    const type = values.type as EarlyRepaymentType | null;
    const unit = values.unit as EarlyRepaymentUnit | null;
    const value = values.value as number | null;
    const months = values.paidPeriodMonths as number | null;

    // A percent is of the remaining principal, so >100 is a typo, not a fee.
    if (unit === EarlyRepaymentUnit.PERCENT && value != null && value > 100) {
      return this.createError({
        path: fieldErrorPath(this.path, "value"),
        message: "Must be <= 100",
      });
    }

    // Absence rules. The input component clears these on a type change, so
    // hitting one means the value arrived from somewhere else (a stale draft,
    // a hand-built payload) and would be saved as unreachable data.
    if (type !== EarlyRepaymentType.FREE_AFTER_PERIOD && months != null) {
      return this.createError({
        path: fieldErrorPath(this.path, "paidPeriodMonths"),
        message: "A fee period applies only to 'Free after a period'",
      });
    }

    if (!NEEDS_FEE(type) && (unit != null || value != null)) {
      return this.createError({
        path: fieldErrorPath(this.path, "value"),
        message:
          "A fee applies only to 'Free after a period' or 'A fee applies'",
      });
    }

    return true;
  },
};

/**
 * Spread into a bid schema: `...EarlyRepaymentValidation()`.
 *
 * The object must be DECLARED here rather than left to arrive untyped —
 * resolvers in this repo run with `stripUnknown: true`, which silently deletes
 * any key the schema does not describe. An undeclared `earlyRepayment` would
 * reach the wire as nothing at all, with no compile error and no 422.
 */
export const EarlyRepaymentValidation = () => ({
  earlyRepayment: yup
    .object({
      type: yup
        .string()
        .oneOf(Object.values(EarlyRepaymentType))
        .required("Cost for early repayment is required"),

      // `.default(null)` on each nullable member, so the validated object
      // always carries all five keys: yup infers `.nullable()` alone as
      // `T | null | undefined`, and an omitted key is not the same thing as a
      // null one on the wire. `.defined()` would also fix the type but makes
      // an omitted key a validation ERROR — this fills it in instead.
      paidPeriodMonths: yup
        .number()
        .nullable()
        .default(null)
        .when("type", {
          is: EarlyRepaymentType.FREE_AFTER_PERIOD,
          then: (s) =>
            s
              .required("Enter how many months the fee applies for")
              .integer("Must be a whole number of months")
              .min(1, "Must be at least 1 month"),
        }),

      unit: yup
        .string()
        .nullable()
        .default(null)
        .oneOf([...Object.values(EarlyRepaymentUnit), null])
        .when("type", {
          is: NEEDS_FEE,
          then: (s) => s.required("Choose a percentage or an amount"),
        }),

      value: yup
        .number()
        .nullable()
        .default(null)
        .when("type", {
          is: NEEDS_FEE,
          then: (s) => s.required("Enter the fee").min(0, "Must be >= 0"),
        }),

      comment: yup.string().nullable().default(null),
    })
    // A FREE bid stores no comment. Rows backfilled by migration 0528 carry
    // their legacy text ("0") as one, so it is dropped on submit rather than
    // rejected; core-service drops it too.
    .transform((terms) =>
      terms?.type === EarlyRepaymentType.FREE
        ? { ...terms, comment: null }
        : terms,
    )
    .test(earlyRepaymentConsistency),
});
